/**
 * 하단 탭 (계획서 §13.5).
 *
 * PPT 는 4개지만 계획서 §13.5 의 5개를 따른다. 우선순위상 계획서가 위다.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { fetchAgents, fetchAudit, fetchOntology, fetchWorkflow } from "@/api/client";
import type { AgentRunView, RunSnapshot } from "@/types/api";
import { agentBadge } from "@/lib/format";
import { EquipmentDetail } from "@/features/process/EquipmentDetail";

import { OntologyGraph } from "./OntologyGraph";
import { WorkflowGraph } from "./WorkflowGraph";

const TABS = ["운전현황", "Agent 상세", "Ontology", "LangGraph", "감사 로그"] as const;
type TabName = (typeof TABS)[number];

interface Props {
  snapshot: RunSnapshot;
}

export function BottomTabs({ snapshot }: Props): React.JSX.Element {
  const [active, setActive] = useState<TabName>("운전현황");

  return (
    <section className="tabs" aria-label="상세 정보">
      <div className="tab-bar" role="tablist">
        {TABS.map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            aria-selected={active === name}
            className={active === name ? "active" : ""}
            onClick={() => {
              setActive(name);
            }}
          >
            {name}
          </button>
        ))}
      </div>

      <div className="tab-body" role="tabpanel" aria-label={active}>
        {active === "운전현황" && <EquipmentDetail equipment={snapshot.equipment} />}
        {active === "Agent 상세" && <AgentTab runId={snapshot.run_id} />}
        {active === "Ontology" && <OntologyTab runId={snapshot.run_id} />}
        {active === "LangGraph" && <WorkflowTab runId={snapshot.run_id} />}
        {active === "감사 로그" && <AuditTab runId={snapshot.run_id} snapshot={snapshot} />}
      </div>
    </section>
  );
}

// --------------------------------------------------------------------------- //
// Agent 상세 (§13.3)
// --------------------------------------------------------------------------- //
function AgentTab({ runId }: { runId: string }): React.JSX.Element {
  const { data, isPending, isError } = useQuery({
    queryKey: ["agents", runId],
    queryFn: () => fetchAgents(runId),
    refetchInterval: 5_000,
  });

  if (isPending) return <p>불러오는 중…</p>;
  if (isError || !data) return <p className="error">Agent 정보를 불러오지 못했습니다.</p>;
  if (data.agents.length === 0) {
    // 분석에는 수 분이 걸린다. "없음" 만 띄우면 고장난 것처럼 보인다.
    return data.analyzing ? <AnalyzingNotice /> : (
      <p className="hint">아직 실행된 Agent 가 없습니다. 고장이 발생하면 실행됩니다.</p>
    );
  }

  return (
    <>
      {data.analyzing && <AnalyzingNotice />}
      <div className="agent-list">
        {data.agents.map((agent) => (
          <AgentCard key={agent.agent_run_id} agent={agent} />
        ))}
      </div>
    </>
  );
}

function AnalyzingNotice(): React.JSX.Element {
  return (
    <p className="analyzing" role="status">
      <span aria-hidden="true">◌</span> Agent 가 분석 중입니다. 설비마다 수십 초가 걸리며
      끝나는 대로 이 자리에 나타납니다.
    </p>
  );
}


function AgentCard({ agent }: { agent: AgentRunView }): React.JSX.Element {
  const badge = agentBadge(agent.status);
  return (
    <article className={`agent-card tone-${badge.tone}`}>
      <header>
        <h3>{agent.agent_id}</h3>
        <span className="badge">
          <span aria-hidden="true">{badge.icon}</span> {badge.label}
        </span>
      </header>
      <dl>
        <div>
          <dt>모델</dt>
          <dd>
            {agent.provider ?? "—"} / {agent.model ?? "—"}
            {agent.used_fallback && <span className="tag">fallback</span>}
          </dd>
        </div>
        <div>
          <dt>elapsed</dt>
          <dd>{agent.elapsed_ms === null ? "—" : `${String(agent.elapsed_ms)}ms`}</dd>
        </div>
        <div>
          <dt>retry</dt>
          <dd>{agent.attempt}회차</dd>
        </div>
        <div>
          <dt>필수 Tool</dt>
          <dd>{agent.required_tool_completed ? "✔ 완료" : "✕ 미완"}</dd>
        </div>
        {agent.error_code && (
          <div>
            <dt>오류</dt>
            <dd>{agent.error_code}</dd>
          </div>
        )}
      </dl>

      {agent.advice && <p className="agent-summary">{agent.advice.display_message}</p>}

      <table className="tool-table">
        <caption>호출한 Tool (순서대로)</caption>
        <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col">Tool</th>
            <th scope="col">상태</th>
            <th scope="col">elapsed</th>
            <th scope="col">fact</th>
          </tr>
        </thead>
        <tbody>
          {agent.tool_calls.map((call, index) => (
            <tr key={call.tool_call_id}>
              <td>{index + 1}</td>
              <td>
                {call.tool_name}
                {call.cache_hit && <span className="tag">cache</span>}
              </td>
              <td>{call.error_code ?? call.status}</td>
              <td>{call.elapsed_ms === null ? "—" : `${String(call.elapsed_ms)}ms`}</td>
              <td>{call.fact_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </article>
  );
}

// --------------------------------------------------------------------------- //
// Ontology (§10, §13.5)
// --------------------------------------------------------------------------- //
function OntologyTab({ runId }: { runId: string }): React.JSX.Element {
  const { data, isPending, isError } = useQuery({
    queryKey: ["ontology", runId],
    queryFn: () => fetchOntology(runId),
    refetchInterval: 10_000,
  });

  if (isPending) return <p>불러오는 중…</p>;
  if (isError || !data) return <p className="error">그래프를 불러오지 못했습니다.</p>;
  if (!data.available) {
    return <p className="hint">Neo4j 를 사용할 수 없어 영향 그래프를 표시하지 않습니다.</p>;
  }
  if (data.nodes.length === 0) {
    return <p className="hint">아직 사고와 연결된 그래프가 없습니다.</p>;
  }

  return <OntologyGraph nodes={data.nodes} relationships={data.relationships} />;
}


// --------------------------------------------------------------------------- //
// LangGraph (§13.5)
// --------------------------------------------------------------------------- //
function WorkflowTab({ runId }: { runId: string }): React.JSX.Element {
  const [selected, setSelected] = useState<string | null>(null);
  const { data, isPending } = useQuery({
    queryKey: ["workflow", runId],
    queryFn: () => fetchWorkflow(runId),
    refetchInterval: 5_000,
  });

  if (isPending) return <p>불러오는 중…</p>;
  if (!data?.available) {
    return <p className="hint">LangGraph checkpointer 를 사용할 수 없어 표시하지 않습니다.</p>;
  }

  return (
    <div className="workflow">
      <dl className="workflow-progress">
        <div>
          <dt>다음 node</dt>
          <dd>{data.next.length > 0 ? data.next.join(", ") : "없음 (완료)"}</dd>
        </div>
        <div>
          <dt>대기 중인 사람 결정</dt>
          <dd>
            {data.interrupts.length > 0
              ? data.interrupts.map((i) => i.kind ?? "?").join(", ")
              : "없음"}
          </dd>
        </div>
        <div>
          <dt>계획 활성화</dt>
          <dd>{data.activated ? "✔ 완료" : "— 대기"}</dd>
        </div>
      </dl>

      <WorkflowGraph
        data={data}
        selected={selected}
        onSelect={(node) => {
          setSelected(selected === node ? null : node);
        }}
      />
      <p className="hint">node 를 클릭하면 그 node 의 실행 결과가 펼쳐집니다 (§13.5)</p>
    </div>
  );
}


// --------------------------------------------------------------------------- //
// 감사 로그 (§13.5)
// --------------------------------------------------------------------------- //
function AuditTab({
  runId,
  snapshot,
}: {
  runId: string;
  snapshot: RunSnapshot;
}): React.JSX.Element {
  const { data } = useQuery({
    queryKey: ["audit", runId],
    queryFn: () => fetchAudit(runId),
    refetchInterval: 5_000,
  });

  const items = data?.items ?? snapshot.audit;
  if (items.length === 0) return <p className="hint">감사 항목이 없습니다.</p>;

  return (
    <table className="audit-table">
      <caption>감사 로그 · rule {snapshot.rule_version} / data {snapshot.data_version}</caption>
      <thead>
        <tr>
          <th scope="col">코드</th>
          <th scope="col">내용</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item, index) => (
          <tr key={`${item.code}-${String(index)}`}>
            <td>{item.code}</td>
            <td>{item.message}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

